import React, { useContext, useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Divider, 
  Fade, 
  Grid, 
  IconButton, 
  Modal, 
  Stack, 
  Tab, 
  Tabs, 
  TextField, 
  Typography 
} from "@mui/material";
import {
  IconArrowLeft,
  IconBookmarks,
  IconPlus,
  IconEdit,
  IconTrash
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import Lottie from "lottie-react";
import animationData from '../../assets/aiden boat.json';
import { Context as AppContext } from '../../context/AppContext';
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import { collection, addDoc, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import ReactMarkdown from 'react-markdown';

const StudentJournal = () => {
  const navigate = useNavigate();
  const { state: { currentUser } } = useContext(AppContext);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [entryTitle, setEntryTitle] = useState('');
  const [entryContent, setEntryContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all', 'learning', 'reflection', 'ideas', 'questions']);

  // Load journal entries
  useEffect(() => {
    if (currentUser) {
      loadJournalEntries();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const loadJournalEntries = async () => {
    try {
      setLoading(true);
      const entriesRef = collection(db, "journal_entries");
      const q = query(
        entriesRef,
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const entries = [];
      
      querySnapshot.forEach((doc) => {
        entries.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        });
      });
      
      setJournalEntries(entries);
    } catch (error) {
      console.error("Error loading journal entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleOpenModal = (entry = null) => {
    if (entry) {
      // Edit mode
      setEditMode(true);
      setCurrentEntry(entry);
      setEntryTitle(entry.title);
      setEntryContent(entry.content);
    } else {
      // New entry mode
      setEditMode(false);
      setCurrentEntry(null);
      setEntryTitle('');
      setEntryContent('');
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEntryTitle('');
    setEntryContent('');
    setCurrentEntry(null);
    setEditMode(false);
  };

  const handleSaveEntry = async () => {
    if (!entryTitle.trim() || !entryContent.trim()) {
      return; // Don't save empty entries
    }

    try {
      if (editMode && currentEntry) {
        // Update existing entry
        await updateDoc(doc(db, "journal_entries", currentEntry.id), {
          title: entryTitle,
          content: entryContent,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new entry
        const category = selectedCategory === 'all' ? 'reflection' : selectedCategory;
        await addDoc(collection(db, "journal_entries"), {
          userId: currentUser.uid,
          title: entryTitle,
          content: entryContent,
          category: category,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      handleCloseModal();
      loadJournalEntries();
    } catch (error) {
      console.error("Error saving journal entry:", error);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this journal entry?")) {
      try {
        await deleteDoc(doc(db, "journal_entries", entryId));
        loadJournalEntries();
      } catch (error) {
        console.error("Error deleting journal entry:", error);
      }
    }
  };

  const filteredEntries = selectedCategory === 'all' 
    ? journalEntries 
    : journalEntries.filter(entry => entry.category === selectedCategory);

  // Group entries by month
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = new Date(entry.createdAt);
    const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    
    groups[monthYear].push(entry);
    return groups;
  }, {});

  return (
    <MainLayout>
      <Box sx={{
        height: 'auto',
        minHeight: '80vh',
        p: 3,
        position: 'relative',
        display: 'flex',
        flexDirection: "column"
      }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'inherit',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <IconArrowLeft />
            </IconButton>
            <Typography variant={'h3'}>
              My AI Learning Journal
            </Typography>
          </Stack>

          <Button
            variant="contained"
            startIcon={<IconPlus />}
            onClick={() => handleOpenModal()}
            sx={{
              background: 'linear-gradient(90deg, #4c81f2, #a670bd)',
              color: 'white',
              fontFamily: 'Poppins',
              '&:hover': {
                background: 'linear-gradient(90deg, #3b70e1, #9560ac)',
              }
            }}
          >
            New Journal Entry
          </Button>
        </Stack>

        {/* Category Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, paddingBottom: 0 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            sx={{
              '& .Mui-selected': {
                background: 'linear-gradient(90deg, #4c81f2, #a670bd, #a670bd)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #4c81f2, #a670bd, #a670bd)',
              }
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={index}
                label={category.charAt(0).toUpperCase() + category.slice(1)}
                value={category}
                sx={{ fontFamily: 'Poppins' }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Journal Entries */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography>Loading your journal entries...</Typography>
          </Box>
        ) : filteredEntries.length > 0 ? (
          Object.entries(groupedEntries).map(([monthYear, entries], groupIndex) => (
            <Box key={groupIndex}>
              {groupIndex > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                {monthYear}
              </Typography>
              <Grid container spacing={2}>
                {entries.map((entry, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Box 
                      sx={{
                        p: 2,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #4c81f2, #a670bd)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {entry.title}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenModal(entry)}
                            sx={{ color: '#4c81f2' }}
                          >
                            <IconEdit size={18} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteEntry(entry.id)}
                            sx={{ color: '#f44336' }}
                          >
                            <IconTrash size={18} />
                          </IconButton>
                        </Stack>
                      </Stack>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        {new Date(entry.createdAt).toLocaleDateString()} • {entry.category}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          overflow: 'hidden', 
                          maxHeight: '150px',
                          flexGrow: 1,
                          '& p': { margin: 0 }
                        }}
                      >
                        <ReactMarkdown>
                          {entry.content.length > 200 
                            ? `${entry.content.substring(0, 200)}...` 
                            : entry.content}
                        </ReactMarkdown>
                      </Box>
                      
                      {entry.content.length > 200 && (
                        <Button 
                          onClick={() => handleOpenModal(entry)} 
                          sx={{ 
                            mt: 1, 
                            alignSelf: 'flex-start',
                            color: '#4c81f2',
                            '&:hover': {
                              background: 'rgba(76, 129, 242, 0.04)'
                            }
                          }}
                        >
                          Read more
                        </Button>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        ) : (
          <Fade in={true} timeout={1000}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
              <Lottie animationData={animationData} loop={true} style={{ height: 200 }} />
              <Typography variant={'h5'} sx={{ mt: 2 }}>
                {currentUser 
                  ? "You haven't created any journal entries yet. Click 'New Journal Entry' to get started!"
                  : "Please sign in to view your journal entries."}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Modal for creating/editing entries */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="journal-entry-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '60%' },
          maxWidth: '800px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="journal-entry-modal-title" variant="h5" component="h2" sx={{ mb: 3 }}>
            {editMode ? 'Edit Journal Entry' : 'New Journal Entry'}
          </Typography>
          
          <TextField
            label="Title"
            fullWidth
            value={entryTitle}
            onChange={(e) => setEntryTitle(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <TextField
            label="Content"
            multiline
            rows={10}
            fullWidth
            value={entryContent}
            onChange={(e) => setEntryContent(e.target.value)}
            placeholder="Write your thoughts, reflections, or questions about AI..."
            sx={{ mb: 3 }}
          />
          
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              sx={{
                borderColor: '#a670bd',
                color: '#a670bd',
                '&:hover': {
                  borderColor: '#9560ac',
                  background: 'rgba(166, 112, 189, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEntry}
              sx={{
                background: 'linear-gradient(90deg, #4c81f2, #a670bd)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(90deg, #3b70e1, #9560ac)',
                }
              }}
            >
              {editMode ? 'Update' : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </MainLayout>
  );
};

export default StudentJournal;
